import type { TicketTypeInsert, TicketType as TicketTypeRow, TicketTypeUpdate } from '#shared/types'

interface TicketTypeFilters {
  campId: string
  isActive?: boolean
  type?: 'week' | 'day'
}

export const useTicketTypes = () => {
  /**
   * Get all ticket types for a camp
   */
  const getTicketTypes = async (filters: TicketTypeFilters) => {
    return await $api<{ data: TicketTypeRow[] }>('/api/ticket-types', {
      method: 'GET',
      params: {
        camp_id: filters.campId,
        active: filters.isActive,
        type: filters.type,
      },
    })
  }

  /**
   * Get a single ticket type by ID
   */
  const getTicketType = async (id: string) => {
    return await $api<{ data: TicketTypeRow }>(`/api/ticket-types/${id}`, {
      method: 'GET',
    })
  }

  /**
   * Create a new ticket type
   */
  const createTicketType = async (data: TicketTypeInsert) => {
    return await $api<{ data: TicketTypeRow }>('/api/ticket-types', {
      method: 'POST',
      body: data,
    })
  }

  /**
   * Update an existing ticket type
   */
  const updateTicketType = async (id: string, updates: TicketTypeUpdate) => {
    return await $api<{ data: TicketTypeRow }>(`/api/ticket-types/${id}`, {
      method: 'PATCH',
      body: updates,
    })
  }

  /**
   * Delete a ticket type
   */
  const deleteTicketType = async (id: string) => {
    return await $api<{ success: boolean }>(`/api/ticket-types/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Get active ticket types for a camp (for booking)
   * Note: This requires authentication, use getPublicTicketTypes for public pages
   */
  const getActiveTicketTypes = async (campId: string) => {
    return await getTicketTypes({ campId, isActive: true })
  }

  /**
   * Get active ticket types for a camp (public - no authentication required)
   * Use this for public-facing pages like camp details
   */
  const getPublicTicketTypes = async (campId: string, inviteToken?: string | null) => {
    return await $fetch<{ data: TicketTypeRow[] }>('/api/ticket-types/public', {
      method: 'GET',
      params: {
        camp_id: campId,
        ...(inviteToken ? { invite_token: inviteToken } : {}),
      },
    })
  }

  /**
   * Get week ticket types for a camp
   */
  const getWeekTicketTypes = async (campId: string) => {
    return await getTicketTypes({ campId, type: 'week' })
  }

  /**
   * Get day ticket types for a camp
   */
  const getDayTicketTypes = async (campId: string) => {
    return await getTicketTypes({ campId, type: 'day' })
  }

  /**
   * Toggle ticket type active status
   */
  const toggleTicketTypeStatus = async (id: string, isActive: boolean) => {
    return await updateTicketType(id, { is_active: isActive })
  }

  /**
   * Calculate available spots for a ticket type
   */
  const calculateAvailableSpots = (ticketType: TicketTypeRow): number | null => {
    if (!ticketType.capacity)
      return null

    const sold = (ticketType.capacity || 0) - (ticketType.available_quantity || 0)
    const reserved = ticketType.reserved_quantity || 0

    return Math.max(0, (ticketType.capacity || 0) - sold - reserved)
  }

  /**
   * Check if ticket type is sold out
   */
  const isSoldOut = (ticketType: TicketTypeRow): boolean => {
    if (!ticketType.capacity)
      return false
    const available = calculateAvailableSpots(ticketType)

    return available !== null && available <= 0
  }

  /**
   * Check if ticket type is on sale
   */
  const isOnSale = (ticketType: TicketTypeRow | { is_active?: boolean | null, sale_start_date?: string | null, sale_end_date?: string | null }): boolean => {
    const now = new Date()

    if (ticketType.sale_start_date && new Date(ticketType.sale_start_date) > now)
      return false

    if (ticketType.sale_end_date && new Date(ticketType.sale_end_date) < now)
      return false

    return (ticketType as { is_active?: boolean | null }).is_active ?? false
  }

  return {
    getTicketTypes,
    getTicketType,
    createTicketType,
    updateTicketType,
    deleteTicketType,
    getActiveTicketTypes,
    getPublicTicketTypes,
    getWeekTicketTypes,
    getDayTicketTypes,
    toggleTicketTypeStatus,
    calculateAvailableSpots,
    isSoldOut,
    isOnSale,
  }
}
